import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FIND_ALL_EXPENSES_WITH_PAGINATION,
  GET_EXPENSE_BY_ID,
  GET_EXPENSE_TYPES,
  GET_PAYMENT_METHODS,
  GET_EXPENSE_STATISTICS,
} from '../graphql/queries/expenses.query';
import {
  CREATE_EXPENSE,
  UPDATE_EXPENSE,
  DELETE_EXPENSE,
  REMOVE_EXPENSE,
  APPROVE_EXPENSE,
  REJECT_EXPENSE,
  EXPORT_EXPENSES_TO_PDF,
  EXPORT_EXPENSES_TO_CSV,
  APPROVE_OR_REJECT_EXPENSE,
} from '../graphql/mutations/expenses.mutation';

export interface Expense {
  id?: number;
  branchId?: number;
  expenseDate: string;
  expenseType: string;
  accountId?: number;
  amount: number;
  tax?: number;
  paidBy?: number;
  approvedBy?: number;
  paidByData?: {
    firstName: string;
  };
  paymentMethod: string;
  bankAccountId?: number;
  creditCardId?: number;
  isCheque?: boolean;
  chequeNo?: string;
  chequeDate?: string;
  referenceNo?: string;
  description?: string;
  attachmentUrl?: string;
  journalId?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  expenseStatus: string;
}

export interface ExpensePaginationInput {
  limit?: number;
  skip?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  filter?: {
    paymentMethod?: string;
    expenseStatus?: string;
  };
  advancedSearch?: {
    expenseType?: string;
    paidByName?: string;
    amount?: string;
  };
}

export interface ExpenseStatisticsInput {
  startDate?: string;
  endDate?: string;
  expenseType?: string;
  paymentMethod?: string;
  expenseStatus?: string;
}

/**
 * Expenses Service
 *
 * This service handles all expense-related operations including:
 * - CRUD operations for expenses
 * - Pagination and filtering
 * - Export functionality
 * - Statistics and reporting
 *
 * Usage Examples:
 *
 * // Get expenses with pagination and filters
 * const input: ExpensePaginationInput = {
 *   limit: 10,
 *   skip: 0,
 *   search: "Office",
 *   startDate: "2025-07-04",
 *   endDate: "2025-07-04",
 *   filter: {
 *     paymentMethod: "CASH",
 *     expenseStatus: "APPROVED"
 *   },
 *   advancedSearch: {
 *     expenseType: "TRAVEL",
 *     paidByName: "John",
 *     amount: "1000"
 *   }
 * };
 *
 * this.expensesService.findAllExpensesWithPagination(input).subscribe(
 *   (result) => {
 *     console.log('Expenses:', result.expenses);
 *     console.log('Total:', result.total);
 *   }
 * );
 *
 * // Get expense by ID
 * this.expensesService.getExpenseById(1).subscribe(
 *   (expense) => {
 *     console.log('Expense details:', expense);
 *   }
 * );
 *
 * // Create new expense
 * const newExpense = {
 *   expenseDate: "2025-01-15",
 *   expenseType: "TRAVEL",
 *   amount: 500,
 *   tax: 50,
 *   paymentMethod: "CASH",
 *   expenseStatus: "PENDING",
 *   description: "Business travel expenses"
 * };
 *
 * this.expensesService.createExpense(newExpense).subscribe(
 *   (result) => {
 *     console.log('Created expense:', result);
 *   }
 * );
 */
@Injectable({
  providedIn: 'root',
})
export class ExpensesService {
  constructor(private apollo: Apollo) {}

  /**
   * Get all expenses with pagination and filtering
   * @param input - Pagination and filter parameters
   * @returns Observable with expenses data and pagination info
   */
  findAllExpensesWithPagination(
    input: ExpensePaginationInput
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: FIND_ALL_EXPENSES_WITH_PAGINATION,
        variables: { input },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findAllExpensesWithPagination || [])
      );
  }

  /**
   * Get expense by ID
   * @param id - Expense ID
   * @returns Observable with expense details
   */
  getExpenseById(id: number): Observable<Expense> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_EXPENSE_BY_ID,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.expense || null));
  }

  /**
   * Create new expense
   * @param input - Expense creation data
   * @returns Observable with created expense
   */
  createExpense(input: any): Observable<Expense> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: CREATE_EXPENSE,
        variables: { input },
      })
      .pipe(map((result: any) => result.data.createExpense || []));
  }

  /**
   * Update existing expense
   * @param id - Expense ID to update
   * @param input - Expense update data
   * @returns Observable with updated expense
   */
  updateExpense(id: number, input: any): Observable<Expense> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: UPDATE_EXPENSE,
        variables: { id, input },
      })
      .pipe(map((result: any) => result.data?.updateExpense || null));
  }

  /**
   * Delete expense
   * @param id - Expense ID to delete
   * @returns Observable with deletion result
   */
  deleteExpense(id: number): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: DELETE_EXPENSE,
        variables: { id },
      })
      .pipe(map((result: any) => result.data.deleteExpense || []));
  }

  /**
   * Remove expense (soft delete)
   * @param id - Expense ID to remove
   * @returns Observable with removal result
   */
  removeExpense(id: number): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: REMOVE_EXPENSE,
        variables: { id },
      })
      .pipe(map((result: any) => result.data.removeExpense || []));
  }

  /**
   * Get all expense types
   * @returns Observable with expense types list
   */
  getExpenseTypes(): Observable<any[]> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_EXPENSE_TYPES,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.getExpenseTypes || []));
  }

  /**
   * Get all payment methods
   * @returns Observable with payment methods list
   */
  getPaymentMethods(): Observable<any[]> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_PAYMENT_METHODS,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.getPaymentMethods || []));
  }

  /**
   * Approve expense
   * @param approveExpenseInput - Approval data
   * @returns Observable with approved expense
   */
  approveExpense(approveExpenseInput: any): Observable<Expense> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: APPROVE_EXPENSE,
        variables: { approveExpenseInput },
      })
      .pipe(map((result: any) => result.data.approveExpense || []));
  }

  /**
   * Reject expense
   * @param rejectExpenseInput - Rejection data with reason
   * @returns Observable with rejected expense
   */
  rejectExpense(rejectExpenseInput: any): Observable<Expense> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: REJECT_EXPENSE,
        variables: { rejectExpenseInput },
      })
      .pipe(map((result: any) => result.data.rejectExpense || []));
  }

  /**
   * Export expenses to PDF
   * @param input - Export filter parameters
   * @param fields - Fields to include in export
   * @returns Observable with PDF export result
   */
  exportExpensesToPDF(input: any, fields: string[]): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: EXPORT_EXPENSES_TO_PDF,
        variables: { input, fields },
      })
      .pipe(map((result: any) => result.data.exportExpensesToPDF || []));
  }

  /**
   * Export expenses to CSV
   * @param input - Export filter parameters
   * @param fields - Fields to include in export
   * @returns Observable with CSV export result
   */
  exportExpensesToCSV(input: any, fields: string[]): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: EXPORT_EXPENSES_TO_CSV,
        variables: { input, fields },
      })
      .pipe(map((result: any) => result.data.exportExpensesToCSV || []));
  }

  /**
   * Get expense statistics
   * @param input - Statistics filter parameters
   * @returns Observable with expense statistics
   */
  getExpenseStatistics(input?: ExpenseStatisticsInput): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_EXPENSE_STATISTICS,
        variables: { input },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.getExpenseStatistics || [])
      );
  }

  approveOrRejectExpense(id: any, input: any): Observable<Expense> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: APPROVE_OR_REJECT_EXPENSE,
        variables: { id, input },
      })
      .pipe(map((result: any) => result.data.modifyExpenseStatus || []));
  }
}
